import React from "react";
import { useTranslation } from "react-i18next";
import {
  PackageCheck, Gauge, Clock, Warehouse, User, Play, Undo2, Volume2, VolumeX, Flag, Store,
} from "lucide-react";
import { Button } from "../../../components/ui/Button";

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="inline-flex items-center gap-2 text-sm text-fg-muted">
        <Icon className="h-4 w-4 text-fg-subtle" />
        {label}
      </span>
      <span className="text-sm font-medium text-fg text-right truncate">{value}</span>
    </div>
  );
}

export default function SessionSummary({
  total, breakdown, ritmo, elapsedLabel, bodegaName, clientName, operatorName, startLabel,
  muted, onToggleMute, onUndoLast, canUndo, onFinish, canFinish,
}) {
  const { t } = useTranslation();

  return (
    <div className="bg-bg border border-border rounded-xl shadow-card p-5 space-y-5 lg:sticky lg:top-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{t("Recibidos en esta sesión")}</p>
          <p className="mt-1 flex items-baseline gap-2">
            <span className="text-5xl font-bold tabular-nums text-fg">{total}</span>
            <span className="text-sm text-fg-muted">{total === 1 ? t("pedido") : t("pedidos")}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onToggleMute}
          aria-pressed={!muted}
          aria-label={muted ? t("Activar sonido") : t("Silenciar")}
          title={muted ? t("Activar sonido") : t("Silenciar")}
          className="rounded-lg p-2 text-fg-muted hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      </div>

      {/* desglose rápido */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-1.5 text-fg-muted">
          <span className="h-2 w-2 rounded-full bg-status-success" />{breakdown.success} {t("OK")}
        </span>
        <span className="inline-flex items-center gap-1.5 text-fg-muted">
          <span className="h-2 w-2 rounded-full bg-status-warning" />{breakdown.duplicate} {t("repetidos")}
        </span>
        <span className="inline-flex items-center gap-1.5 text-fg-muted">
          <span className="h-2 w-2 rounded-full bg-status-error" />{breakdown.notfound} {t("sin match")}
        </span>
      </div>

      {/* ritmo + tiempo */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-bg-muted px-3 py-2.5">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
            <Gauge className="h-3.5 w-3.5" />{t("Ritmo")}
          </p>
          <p className="mt-0.5 text-lg font-semibold text-fg tabular-nums">{ritmo} <span className="text-xs font-normal text-fg-muted">{t("ped/min")}</span></p>
        </div>
        <div className="rounded-lg bg-bg-muted px-3 py-2.5">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
            <Clock className="h-3.5 w-3.5" />{t("Activa")}
          </p>
          <p className="mt-0.5 text-lg font-semibold text-fg tabular-nums">{elapsedLabel}</p>
        </div>
      </div>

      {/* meta */}
      <div className="divide-y divide-border border-t border-border">
        <MetaRow icon={Store} label={t("Cliente")} value={clientName || t("Sin asignar")} />
        <MetaRow icon={Warehouse} label={t("Bodega")} value={bodegaName} />
        <MetaRow icon={User} label={t("Operador")} value={operatorName} />
        <MetaRow icon={Play} label={t("Inicio")} value={startLabel} />
      </div>

      {/* acciones */}
      <div className="space-y-2">
        <Button variant="outline" fullWidth iconLeft={<Undo2 className="h-4 w-4" />} onClick={onUndoLast} disabled={!canUndo}>
          {t("Deshacer último")}
        </Button>
        <Button variant="primary" fullWidth iconLeft={<Flag className="h-4 w-4" />} onClick={onFinish} disabled={!canFinish}>
          {t("Finalizar y ver resumen")}
        </Button>
      </div>
    </div>
  );
}
